import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { supportAPI } from '@/services/api';
import { LifeBuoy, Send, Mail, Plus, ArrowLeft, Loader2, MessageCircle, X } from 'lucide-react';

export default function ContactUs() {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  if (ticketId) return <TicketDetailView ticketId={ticketId} onBack={() => navigate('..')} />;

  return <TicketsListView onOpen={(id) => navigate(`ticket/${id}`)} />;
}

function TicketsListView({ onOpen }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await supportAPI.myTickets({ limit: 50 }); setTickets(d.tickets || []); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await supportAPI.createTicket(subject, body);
      setSubject(''); setBody(''); setShowForm(false);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="contact-us-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-slate-900 dark:text-white flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-emerald-600 dark:text-emerald-400" /> Contact Numuv Support</h1>
        <p className="text-slate-500 dark:text-zinc-400 text-sm mt-2">Raise a ticket below — our support team typically responds within one business day.</p>
        <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1">
          Prefer email? Reach us at <a href="mailto:support@numuv.app" className="text-emerald-600 dark:text-emerald-400 hover:underline">support@numuv.app</a>
        </p>
      </div>

      <button onClick={() => setShowForm(!showForm)} className="mb-5 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-full text-sm font-medium flex items-center gap-2" data-testid="new-ticket-toggle">
        <Plus className="w-4 h-4" /> {showForm ? 'Cancel' : 'New ticket'}
      </button>

      {showForm && (
        <form onSubmit={submit} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5 mb-6 space-y-3" data-testid="new-ticket-form">
          <div>
            <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1">Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} required maxLength={200}
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
              placeholder="A short summary" data-testid="ticket-subject-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1">Details</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} required rows={5} maxLength={4000}
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
              placeholder="Describe your issue in detail…" data-testid="ticket-body-input" />
          </div>
          <button type="submit" disabled={submitting} className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2" data-testid="ticket-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit ticket
          </button>
        </form>
      )}

      <h2 className="text-slate-900 dark:text-white text-sm font-medium mb-3">Your tickets</h2>
      <div className="space-y-2">
        {loading && <p className="text-slate-500 dark:text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</p>}
        {!loading && tickets.length === 0 && <p className="text-slate-500 dark:text-zinc-500 text-sm bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-6 text-center">You haven&apos;t raised any tickets yet.</p>}
        {tickets.map(t => (
          <button key={t.id} onClick={() => onOpen(t.id)} className="w-full text-left bg-white dark:bg-[#111114] hover:bg-slate-50 dark:hover:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 transition-colors" data-testid={`ticket-${t.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-slate-900 dark:text-white text-sm font-medium truncate">{t.subject}</p>
                <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1">#{t.id.slice(-6)} · {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : ''}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${t.status === 'open' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-slate-200 dark:bg-zinc-700/40 text-slate-600 dark:text-zinc-300'}`}>{t.status}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TicketDetailView({ ticketId, onBack }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { setData(await supportAPI.getTicket(ticketId)); }
    catch { onBack(); }
  };

  useEffect(() => { load(); }, [ticketId]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try { await supportAPI.postMessage(ticketId, reply); setReply(''); await load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
    setSending(false);
  };

  const close = async () => {
    if (!window.confirm('Mark this ticket as resolved?')) return;
    try { await supportAPI.closeTicket(ticketId); await load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
  };

  if (!data) return <div className="p-6 text-slate-500 dark:text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>;
  const t = data.ticket;
  const isOpen = t.status === 'open';
  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="ticket-detail-page">
      <button onClick={onBack} className="text-slate-500 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300 text-sm inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Back to tickets</button>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">{t.subject}</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1">#{t.id.slice(-6)} · Opened {new Date(t.created_at).toLocaleString()} · <span className={isOpen ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-zinc-400'}>{t.status}</span></p>
        </div>
        {isOpen && <button onClick={close} className="text-xs text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400 flex items-center gap-1" data-testid="close-ticket-btn"><X className="w-3 h-3" /> Mark resolved</button>}
      </div>

      <div className="space-y-3 mb-5">
        {data.messages.map(m => (
          <div key={m.id} className={`rounded-lg p-3 max-w-[85%] ${m.sender === 'support' ? 'bg-emerald-500/5 border border-emerald-500/20 ml-auto' : 'bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50'}`} data-testid={`message-${m.id}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`text-xs font-medium ${m.sender === 'support' ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-zinc-300'}`}>{m.sender_display_name}</span>
              <span className="text-slate-500 dark:text-zinc-600 text-[10px]">{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <p className="text-slate-600 dark:text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
          </div>
        ))}
      </div>

      {isOpen ? (
        <form onSubmit={send} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-4">
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} maxLength={4000}
            placeholder="Type your reply…" className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
            data-testid="reply-input" />
          <div className="flex justify-end mt-2">
            <button type="submit" disabled={sending || !reply.trim()} className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2" data-testid="reply-submit-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
            </button>
          </div>
        </form>
      ) : (
        <p className="text-slate-500 dark:text-zinc-500 text-sm bg-slate-50 dark:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 text-center">This ticket is closed. Open a new one if you need further help.</p>
      )}
    </div>
  );
}
